import React, { useEffect, useState } from 'react';
import config from '../config';

interface Category {
  id: number;
  name: string;
  subCategories?: Category[];
}

interface Diagram {
  id: number;
  diagramType: string;
  defaultCaption?: string | null;
}

interface CategoryDiagram {
  id: number;
  categoryId: number;
  categoryName: string;
  diagramId: number;
  diagramType: string;
  placementType: number;
  displaySize: number;
  displayOrder: number;
  introHtml?: string | null;
  captionOverride?: string | null;
  isEnabled: boolean;
}

const placementOptions = [
  { value: 1, label: 'After Overview' },
  { value: 2, label: 'After Tips' },
  { value: 3, label: 'Before Examples' },
  { value: 4, label: 'After Examples' },
  { value: 5, label: 'End Of Chapter' },
];

const sizeOptions = [
  { value: 1, label: 'Small' },
  { value: 2, label: 'Medium' },
  { value: 3, label: 'Large' },
  { value: 4, label: 'Full Width' },
];

const ManageCategoryDiagrams: React.FC = () => {
  const [items, setItems] = useState<CategoryDiagram[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [diagrams, setDiagrams] = useState<Diagram[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const isMobile = windowWidth <= 480;
  const isTablet = windowWidth <= 768;

  const [formData, setFormData] = useState({
    id: 0,
    categoryId: 0,
    diagramId: 0,
    placementType: 1,
    displaySize: 2,
    displayOrder: 1,
    introHtml: '',
    captionOverride: '',
    isEnabled: true,
  });

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadAll();
  }, []);

  const flattenCategories = (tree: Category[]): Category[] => {
    const flattened: Category[] = [];

    const walk = (nodes: Category[]) => {
      nodes.forEach((node) => {
        flattened.push({ id: node.id, name: node.name });
        if (node.subCategories && node.subCategories.length > 0) {
          walk(node.subCategories);
        }
      });
    };

    walk(tree);
    return flattened;
  };

  const loadAll = async (preferredItemId?: number | null) => {
    try {
      setLoading(true);

      const [itemsResp, categoriesResp, diagramsResp] = await Promise.all([
        fetch(`${config.apiBaseUrl}/api/categorydiagrams`),
        fetch(`${config.apiBaseUrl}/api/categories`),
        fetch(`${config.apiBaseUrl}/api/diagrams`),
      ]);

      if (!itemsResp.ok) throw new Error('Failed to load category diagram associations');
      if (!categoriesResp.ok) throw new Error('Failed to load categories');
      if (!diagramsResp.ok) throw new Error('Failed to load diagrams');

      const itemsData: CategoryDiagram[] = await itemsResp.json();
      const categoriesData: Category[] = await categoriesResp.json();
      const diagramsData: Diagram[] = await diagramsResp.json();

      const allCategories = flattenCategories(categoriesData);

      setItems(itemsData);
      setCategories(allCategories);
      setDiagrams(diagramsData);

      if (itemsData.length === 0) {
        setEditingId(null);
      } else if (preferredItemId) {
        const matched = itemsData.find((x) => x.id === preferredItemId);
        if (matched) {
          handleEdit(matched);
        } else {
          handleEdit(itemsData[0]);
        }
      } else if (!isAdding && editingId === null) {
        handleEdit(itemsData[0]);
      }

      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item: CategoryDiagram) => {
    setFormData({
      id: item.id,
      categoryId: item.categoryId,
      diagramId: item.diagramId,
      placementType: item.placementType,
      displaySize: item.displaySize || 2,
      displayOrder: item.displayOrder,
      introHtml: item.introHtml || '',
      captionOverride: item.captionOverride || '',
      isEnabled: item.isEnabled,
    });
    setEditingId(item.id);
    setIsAdding(false);
  };

  const handleAddNew = () => {
    const defaultCategoryId = categories.length > 0 ? categories[0].id : 0;
    const defaultDiagramId = diagrams.length > 0 ? diagrams[0].id : 0;

    setFormData({
      id: 0,
      categoryId: defaultCategoryId,
      diagramId: defaultDiagramId,
      placementType: 1,
      displaySize: 2,
      displayOrder: 1,
      introHtml: '',
      captionOverride: '',
      isEnabled: true,
    });

    setEditingId(null);
    setIsAdding(true);
  };

  const handleCancel = () => {
    setIsAdding(false);

    if (items.length > 0) {
      handleEdit(items[0]);
    } else {
      setEditingId(null);
    }
  };

  const handleSave = async () => {
    if (!formData.categoryId) {
      setError('Category is required');
      return;
    }

    if (!formData.diagramId) {
      setError('Diagram is required');
      return;
    }

    if (!formData.displayOrder || formData.displayOrder <= 0) {
      setError('Display order must be greater than 0');
      return;
    }

    try {
      const payload = {
        categoryId: formData.categoryId,
        diagramId: formData.diagramId,
        placementType: formData.placementType,
        displaySize: formData.displaySize,
        displayOrder: formData.displayOrder,
        introHtml: formData.introHtml || null,
        captionOverride: formData.captionOverride || null,
        isEnabled: formData.isEnabled,
      };

      const url = isAdding
        ? `${config.apiBaseUrl}/api/categorydiagrams`
        : `${config.apiBaseUrl}/api/categorydiagrams/${formData.id}`;

      const method = isAdding ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to save association');
      }

      if (isAdding) {
        const created: CategoryDiagram = await response.json();
        await loadAll(created.id);
      } else {
        await loadAll(formData.id);
      }

      setIsAdding(false);
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to save association');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this association?')) return;

    try {
      const response = await fetch(`${config.apiBaseUrl}/api/categorydiagrams/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete association');

      await loadAll();
      if (editingId === id) {
        setEditingId(null);
        setIsAdding(false);
      }
      setError(null);
    } catch {
      setError('Failed to delete association');
    }
  };

  const getPlacementLabel = (placementValue: number) => {
    return placementOptions.find((x) => x.value === placementValue)?.label || `Unknown (${placementValue})`;
  };

  const selectedItem = items.find((x) => x.id === editingId);

  return (
    <div
      style={{
        maxWidth: 1400,
        margin: '0 auto',
        padding: isMobile ? '16px 8px' : isTablet ? '20px 12px' : '24px 20px',
        textAlign: 'left',
      }}
    >
      <h1
        style={{
          fontSize: isMobile ? 24 : isTablet ? 28 : 32,
          fontWeight: 700,
          marginBottom: isMobile ? 16 : 24,
          color: '#222',
        }}
      >
        Manage Category Diagrams
      </h1>

      {error && (
        <div
          style={{
            background: '#ffebee',
            color: '#c62828',
            padding: isMobile ? 12 : 16,
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: 24,
          flexDirection: isMobile || isTablet ? 'column' : 'row',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <button
            onClick={handleAddNew}
            style={{
              marginBottom: 24,
              padding: isMobile ? '10px 20px' : '12px 24px',
              backgroundColor: '#4caf50',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: isMobile ? 13 : 14,
              fontWeight: 600,
            }}
          >
            ➕ Add Association
          </button>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#666' }}>Loading associations...</div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#999' }}>No category-diagram associations</div>
          ) : (
            <div style={{ marginLeft: isMobile ? 0 : 16 }}>
              <h2 style={{ fontSize: isMobile ? 16 : 18, fontWeight: 600, marginBottom: 16, color: '#222' }}>
                {items.length} Associations
              </h2>
              {items.map((item) => {
                const isEditing = editingId === item.id;
                return (
                  <div
                    key={item.id}
                    onClick={() => handleEdit(item)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: isMobile ? 12 : 16,
                      backgroundColor: isEditing ? '#e3f2fd' : '#f8fafc',
                      border: `2px solid ${isEditing ? '#2196f3' : '#94a3b8'}`,
                      borderRadius: 6,
                      marginBottom: 8,
                      flexWrap: 'wrap',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: isMobile ? '100%' : '220px' }}>
                      <p style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, margin: 0, color: '#222' }}>
                        {item.categoryName} → {item.diagramType}
                      </p>
                      <p style={{ fontSize: isMobile ? 12 : 13, color: '#666', margin: '4px 0 0 0' }}>
                        {getPlacementLabel(item.placementType)} • {sizeOptions.find((x) => x.value === item.displaySize)?.label || 'Medium'} • Order {item.displayOrder}
                      </p>
                      <p style={{ fontSize: isMobile ? 11 : 12, color: '#999', margin: '4px 0 0 0' }}>
                        {item.isEnabled ? '✅ Enabled' : '⛔ Disabled'}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item.id);
                      }}
                      style={{
                        padding: isMobile ? '6px 12px' : '8px 16px',
                        backgroundColor: '#f44336',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: isMobile ? 12 : 13,
                        fontWeight: 600,
                      }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div
          style={{
            flex: 1,
            minWidth: isMobile || isTablet ? '100%' : '350px',
            maxWidth: isMobile || isTablet ? '100%' : '520px',
            backgroundColor: '#f5f5f5',
            border: '2px solid #2196f3',
            borderRadius: 8,
            padding: isMobile ? 16 : 24,
            height: 'fit-content',
            position: isMobile || isTablet ? 'relative' : 'sticky',
            top: 20,
          }}
        >
          {isAdding || editingId !== null ? (
            <>
              <h2 style={{ fontSize: isMobile ? 18 : 20, fontWeight: 600, marginBottom: 16, color: '#222' }}>
                {isAdding ? 'Add Association' : 'Edit Association'}
              </h2>

              {!isAdding && selectedItem && (
                <div
                  style={{
                    background: '#fff',
                    borderRadius: 6,
                    padding: 12,
                    marginBottom: 16,
                    border: '1px solid #ddd',
                  }}
                >
                  <div style={{ fontWeight: 600, color: '#333' }}>{selectedItem.categoryName}</div>
                  <div style={{ color: '#666', fontSize: 13 }}>{selectedItem.diagramType}</div>
                </div>
              )}

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>Category</label>
                <select
                  value={formData.categoryId}
                  onChange={(e) => setFormData({ ...formData, categoryId: Number(e.target.value) })}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                >
                  <option value={0}>Select category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>Diagram</label>
                <select
                  value={formData.diagramId}
                  onChange={(e) => setFormData({ ...formData, diagramId: Number(e.target.value) })}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                >
                  <option value={0}>Select diagram</option>
                  {diagrams.map((diagram) => (
                    <option key={diagram.id} value={diagram.id}>
                      {diagram.diagramType} {diagram.defaultCaption ? `— ${diagram.defaultCaption}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>Placement Type</label>
                <select
                  value={formData.placementType}
                  onChange={(e) => setFormData({ ...formData, placementType: Number(e.target.value) })}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                >
                  {placementOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>Display Order</label>
                <input
                  type="number"
                  min={1}
                  value={formData.displayOrder}
                  onChange={(e) => setFormData({ ...formData, displayOrder: Number(e.target.value) || 1 })}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>Display Size</label>
                <select
                  value={formData.displaySize}
                  onChange={(e) => setFormData({ ...formData, displaySize: Number(e.target.value) })}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                >
                  {sizeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>Caption Override</label>
                <input
                  type="text"
                  value={formData.captionOverride}
                  onChange={(e) => setFormData({ ...formData, captionOverride: e.target.value })}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc' }}
                  placeholder="Optional custom caption"
                />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>Intro HTML</label>
                <textarea
                  value={formData.introHtml}
                  onChange={(e) => setFormData({ ...formData, introHtml: e.target.value })}
                  rows={5}
                  style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', resize: 'vertical' }}
                  placeholder="Optional introductory HTML before diagram"
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#333', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={formData.isEnabled}
                    onChange={(e) => setFormData({ ...formData, isEnabled: e.target.checked })}
                  />
                  Enabled
                </label>
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  onClick={handleSave}
                  style={{
                    padding: isMobile ? '8px 16px' : '10px 20px',
                    backgroundColor: '#2196f3',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  💾 Save
                </button>
                <button
                  onClick={handleCancel}
                  style={{
                    padding: isMobile ? '8px 16px' : '10px 20px',
                    backgroundColor: '#9e9e9e',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', color: '#666', padding: 20 }}>
              Select an association from the left to edit, or click "Add Association".
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageCategoryDiagrams;
